// exp_PORTFOLIO-61587_file
import { FC, useEffect, useRef, useState } from 'react';

import desktopImage from 'assets/vacancy_of_the_day_desktop.jpg';
import mobileImage from 'assets/vacancy_of_the_day_mobile.jpg';

import {
    Card,
    MediaViewer,
    NavBar,
    ScrollableContainer,
    type ScrollableControls,
    Text,
    VSpacing,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import styles from 'components/VacancyOfTheDayBuilder/components/ExamplesCarousel.module.less';

const TrlKeys = {
    title: 'VacancyOfTheDayPage.info.title',
    description: 'VacancyOfTheDayPage.info.description',
};

const SLIDES = [mobileImage, desktopImage];
const SLIDE_DURATION_MS = 5000;
const PROGRESS_TICK_MS = 50;

export const ExamplesCarousel: FC = () => {
    const controlsRef = useRef<ScrollableControls>(null);
    const indexRef = useRef(0);
    const startedAtRef = useRef(0);

    const [progress, setProgress] = useState(0);
    const [autoplay, setAutoplay] = useState(true);
    const [viewerIndex, setViewerIndex] = useState<number | null>(null);

    const trls = useTrls();

    const handleDotChange = (dot: number) => {
        indexRef.current = dot - 1;
        startedAtRef.current = performance.now();
        setProgress(0);
    };

    useEffect(() => {
        const media = window.matchMedia('(prefers-reduced-motion: reduce)');
        const syncAutoplay = () => setAutoplay(!media.matches);
        syncAutoplay();
        media.addEventListener('change', syncAutoplay);
        return () => media.removeEventListener('change', syncAutoplay);
    }, []);

    useEffect(() => {
        if (!autoplay || viewerIndex !== null) {
            return undefined;
        }

        startedAtRef.current = performance.now();
        const timer = window.setInterval(() => {
            const elapsed = performance.now() - startedAtRef.current;
            if (elapsed >= SLIDE_DURATION_MS) {
                const nextIndex = (indexRef.current + 1) % SLIDES.length;
                indexRef.current = nextIndex;
                startedAtRef.current = performance.now();
                setProgress(0);
                controlsRef.current?.scrollToIndex(nextIndex);
                return;
            }
            setProgress(elapsed);
        }, PROGRESS_TICK_MS);

        return () => window.clearInterval(timer);
    }, [autoplay, viewerIndex]);

    return (
        <Card padding={24} stretched borderRadius={20} borderWidth="default" data-qa="vacancy-of-the-day-carousel">
            <Text typography="subtitle-1-semibold">{trls(TrlKeys.title)}</Text>
            <VSpacing default={8} />
            <Text typography="label-2-regular">{trls(TrlKeys.description)}</Text>
            <VSpacing default={16} />
            <ScrollableContainer ref={controlsRef} scrollSnapAlign="start">
                <ScrollableContainer.Gallery style={{ paddingTop: 0, paddingBottom: 0, marginTop: 0, marginBottom: 0 }}>
                    <div className={styles.slides}>
                        {SLIDES.map((slide, index) => (
                            <ScrollableContainer.Item key={slide} className={styles.slide}>
                                <button
                                    type="button"
                                    className={styles.imageButton}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setViewerIndex(index);
                                    }}
                                >
                                    <img className={styles.image} src={slide} />
                                </button>
                            </ScrollableContainer.Item>
                        ))}
                    </div>
                </ScrollableContainer.Gallery>
                <ScrollableContainer.DotPages
                    offsetTop={16}
                    onChangeDot={handleDotChange}
                    dotPagesProps={{
                        activeDynamic: autoplay,
                        progressProps: {
                            progress,
                            max: SLIDE_DURATION_MS,
                        },
                        'data-qa': 'vacancy-of-the-day-carousel-dots',
                    }}
                />
            </ScrollableContainer>
            <MediaViewer
                visible={viewerIndex !== null}
                initialItemIndex={viewerIndex ?? 0}
                onClose={() => setViewerIndex(null)}
            >
                <MediaViewer.Header>
                    <NavBar>
                        <NavBar.Pane>
                            <NavBar.Actions
                                right={
                                    <MediaViewer.CloseTool>
                                        <CrossOutlinedSize24
                                            initialColor="constant"
                                            padding={8}
                                            borderRadius="halfHeight"
                                            tabIndex={0}
                                        />
                                    </MediaViewer.CloseTool>
                                }
                            />
                        </NavBar.Pane>
                    </NavBar>
                </MediaViewer.Header>
                <MediaViewer.Gallery>
                    {SLIDES.map((slide) => (
                        <MediaViewer.GalleryItem key={slide} src={slide} />
                    ))}
                </MediaViewer.Gallery>
                <MediaViewer.Footer>
                    <MediaViewer.Preview />
                    <MediaViewer.Toolbar />
                </MediaViewer.Footer>
            </MediaViewer>
        </Card>
    );
};
